import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { base64ToBytes } from "@/lib/base64Utils";

export interface PreviewState {
  fileUrl: string;
  fileName?: string;
}

interface FilePreviewOptions {
  items?: { key: string }[];
  onError?: (message: string) => void;
}

export interface PendingPreviewFile {
  content: string;
  filename: string;
  mimetype: string;
}

export const useFilePreview = (options: FilePreviewOptions = {}) => {
  const { items, onError } = options;
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const blobUrlRef = useRef<string | null>(null);

  const validKeysSet = useMemo(
    () => (items ? new Set(items.map((item) => item.key)) : null),
    [items],
  );

  useEffect(() => {
    if (!validKeysSet) return;
    setExpandedKeys((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter((key) => validKeysSet.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [validKeysSet]);

  const resetPreview = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPreview(null);
  }, []);

  const openBase64Preview = useCallback(
    async ({ content, filename, mimetype }: PendingPreviewFile) => {
      try {
        resetPreview();
        const bytes = await base64ToBytes(content);
        const blob = new Blob([bytes as BlobPart], { type: mimetype });
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setPreview({ fileUrl: blobUrl, fileName: filename });
      } catch (error) {
        onError?.(error instanceof Error ? error.message : String(error));
      }
    },
    [onError, resetPreview],
  );

  const openPreviewDialog = useCallback(
    (fileUrl: string | null, fileName?: string) => {
      if (!fileUrl) return;
      resetPreview();
      setPreview({ fileUrl, fileName });
    },
    [resetPreview],
  );

  const closePreview = useCallback(() => {
    resetPreview();
  }, [resetPreview]);

  // Cleanup: revoke blob URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      resetPreview();
    };
  }, [resetPreview]);

  const toggleInlinePreview = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  return {
    preview,
    expandedKeys,
    toggleInlinePreview,
    openPreviewDialog,
    openBase64Preview,
    closePreview,
    setPreview,
    setExpandedKeys,
  };
};
